/**
 * Center a fixed help/popover panel in the usable viewport.
 * Reserves the top navbar and (on mobile) the bottom tab bar.
 * Does not reserve the Picks sticky TP meter — the help panel stacks above it.
 *
 * @param {{
 *   innerWidth: number,
 *   innerHeight: number,
 *   mainContentRect?: { left: number, width: number } | null,
 *   topNavHeight?: number,
 *   bottomNavHeight?: number,
 *   edgePad?: number
 * }} opts
 */
export function computeCenteredPopoverDock({
  innerWidth,
  innerHeight,
  mainContentRect = null,
  topNavHeight = 0,
  bottomNavHeight = 0,
  edgePad = 16
}) {
  const width = mainContentRect ? mainContentRect.width : Math.min(innerWidth - 24, 480);
  const centerLeft = mainContentRect
    ? mainContentRect.left + mainContentRect.width / 2
    : innerWidth / 2;

  const topReserve = Math.max(0, topNavHeight);
  const bottomReserve = Math.max(0, bottomNavHeight);
  const usableTop = topReserve + edgePad;
  const usableBottom = innerHeight - bottomReserve - edgePad;
  const usableHeight = Math.max(120, usableBottom - usableTop);
  const maxHeight = Math.min(innerHeight * 0.85, usableHeight);
  const top = (usableTop + usableBottom) / 2;

  return {
    left: centerLeft,
    width,
    top,
    maxHeight,
    transform: 'translate(-50%, -50%)'
  };
}

/** z-index for AugmentHelpPopover — must beat Picks sticky meter (52) and nav (100). */
export const AUGMENT_HELP_POPOVER_Z_INDEX = 10060;

/**
 * Top navbar bottom edge (px from viewport top). Sticky TP meter is intentionally
 * excluded — help overlays it via z-index instead of docking below it (docking
 * under an in-flow/stuck meter made the tooltip look cut on top).
 * @param {Window} [win]
 * @param {Document} [doc]
 */
export function getTopNavReserve(win = window, doc = document) {
  if (typeof win === 'undefined' || typeof doc === 'undefined') return 0;
  const nav = doc.querySelector?.('.sc-navbar');
  if (nav) {
    const bottom = nav.getBoundingClientRect().bottom;
    if (Number.isFinite(bottom) && bottom > 0) return bottom;
  }
  // Approx --sc-nav-height without safe-area: 0.5rem + 2.25rem + 0.5rem + 1px
  return 53;
}

/**
 * Bottom tab bar reserve for docked popovers (0 on desktop where nav is hidden).
 * @param {Window} [win]
 * @param {Document} [doc]
 */
export function getMobileBottomNavReserve(win = window, doc = document) {
  if (typeof win === 'undefined' || typeof doc === 'undefined') return 0;
  if (typeof win.matchMedia === 'function' && win.matchMedia('(min-width: 768px)').matches) {
    return 0;
  }
  const rootStyle = win.getComputedStyle(doc.documentElement);
  const bottomNav = parseFloat(rootStyle.getPropertyValue('--sc-bottom-nav-height'));
  const safeBottom = parseFloat(rootStyle.getPropertyValue('--sc-safe-bottom'));
  return (Number.isFinite(bottomNav) ? bottomNav : 72) + (Number.isFinite(safeBottom) ? safeBottom : 0);
}
